import pRetry from 'p-retry';
import type { Options } from 'p-retry';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { QuerySitesRequest } from '@wix/ambassador-site-list-v2-site/types';
import { querySites } from '@wix/ambassador-site-list-v2-site/http';
import type { HttpClient } from '@wix/cli-http-client';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { querySitesSchema } from './schemas.js';

export class SiteListClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    const { minRetryTimeout } = getTestOverrides();

    this.retryOptions = {
      retries: 3,
      minTimeout: minRetryTimeout,
      maxTimeout: 3 * 1000,
    };
  }

  querySites = async (opts: QuerySitesRequest = {}) => {
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(querySites(opts)),
        this.retryOptions
      );

      return querySitesSchema.parse(data).sites;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToQuerySites(),
        cause: e,
        info: { ...opts },
      });
    }
  };

  getSite = async (id: string, inclHeadless?: boolean) => {
    const queryPayload: QuerySitesRequest = {
      query: {
        filter: {
          id: { $in: [id] },
          ...(inclHeadless
            ? {
                namespace: { $in: ['WIX', 'HEADLESS'] },
              }
            : {}),
        },
      },
    };
    try {
      const { data } = await pRetry(
        () => this.httpClient.request(querySites(queryPayload)),
        this.retryOptions
      );

      const { sites } = querySitesSchema.parse(data);
      return sites.at(0) ?? null;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetSite(),
        cause: e,
        info: { id },
      });
    }
  };
}
